<template>
  <v-app light class="leaply app">
    <Navigation v-if="!minimalLayout" :location="location" />

    <v-toolbar
      :class="$vuetify.breakpoint.mdAndUp ? 'transparent' : 'grey lighten-5'"
      class="elevation-0"
      style="z-index: 3"
      :height="64"
      absolute
      app
    >
      <v-toolbar-side-icon
        v-if="!minimalLayout && loggedIn"
        @click.stop="$store.commit('toggleDrawer')"
        :color="whiteLogo ? 'white' : 'transparent'"
      />

      <Logo
        :height="30"
        :letters="whiteLogo ? 'white' : '#424242'"
        :dot="whiteLogo ? 'white' : '#FF5722'"
        class="mt-2"
      />

      <LocationSelect
        v-if="!minimalLayout && loggedIn && role === 'candidate'"
        @location="handleLocationSelect"
        solo
        app
      />

      <v-spacer />

      <v-btn
        v-if="!minimalLayout && loggedIn && role === 'client' && isPaymentWhitelisted"
        to="/client/billing"
        flat
        :dark="whiteLogo"
      >
        <v-icon left small>mdi-credit-card</v-icon>
        Billing
      </v-btn>

      <v-btn
        v-if="!minimalLayout && !loggedIn && $vuetify.breakpoint.smAndUp"
        v-text="'Employers'"
        to="/employers"
        color="white"
        flat
      />

      <!-- Sign up -->
      <v-btn
        v-if="!minimalLayout && !loggedIn"
        flat
        @click="signup = true"
        :dark="whiteLogo"
      >
        Sign up
      </v-btn>

      <v-menu
        v-if="!minimalLayout && !loggedIn && $route.path != '/login'"
        :min-width="$vuetify.breakpoint.smAndUp ? '400px' : '95vw'"
        :close-on-content-click="false"
        v-model="showLogin"
        offset-y
        left
      >
        <v-btn flat outline slot="activator" :dark="whiteLogo">
          Log in
        </v-btn>
        <v-card tile>
          <v-card-title
            v-text="'Log in'"
            :class="themes.candidate.light.dialogTitle"
            class="title"
          />
          <v-card-text>
            <Login v-if="showLogin" @close="showLogin = false" />
          </v-card-text>
        </v-card>
      </v-menu>

      <CandidateNavigationBar
        v-if="
          !minimalLayout &&
            loggedIn &&
            role === 'candidate' &&
            $vuetify.breakpoint.mdAndUp
        "
      />
    </v-toolbar>

    <template v-if="!minimalLayout && loggedIn && role === 'candidate'">
      <EmailConfirmationBanner />
      <SuspendedBanner />
    </template>

    <router-view @signup="signup = true" />

    <v-bottom-sheet :value="update.available && !update.snoozed" persistent>
      <v-list>
        <v-subheader>Application update is available</v-subheader>
        <v-list-tile @click="$store.dispatch('cacherefresh')">
          <v-list-tile-avatar>
            <v-icon color="success">mdi-check</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-title>Update now</v-list-tile-title>
        </v-list-tile>
        <v-list-tile @click="update.snoozed = true" class="grey--text">
          <v-list-tile-avatar>
            <v-icon color="grey">mdi-alarm-snooze</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-title>Update later</v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-bottom-sheet>

    <v-dialog
      v-model="signup"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="80%"
      persistent
    >
      <v-card tile class="signup-dialog" style="background: rgba(0,0,0,0.7);">
        <v-card-title class="pb-0">
          <v-spacer />
          <v-btn icon dark @click="signup = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <Signup v-if="signup" @close="signup = false" />
      </v-card>
    </v-dialog>

    <v-snackbar
      v-if="snackbar"
      :timeout="snackbar.timeout"
      :color="snackbar.type"
      :value="snackbar"
      bottom
    >
      {{ snackbar.text }}
      <v-btn
        v-if="snackbar.button && snackbar.button.link"
        v-text="snackbar.button.text"
        :to="snackbar.button.link"
        small
        flat
      />
      <v-btn
        v-if="snackbar.button && snackbar.button.function"
        @click="snackbar.button.function"
        v-text="snackbar.button.text"
        small
        flat
      />
      <v-btn v-else small flat fab @click="$store.commit('snackbar', null)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-footer
      :color="
        role === 'candidate'
          ? themes.candidate.light.footer
          : themes.client.light.primary
      "
      class="footer"
    >
      <v-flex xs12 class="caption grey--text text-xs-center">
        &copy; {{ new Date().getFullYear() }} Leaply Corporation (Pty) Ltd. All
        rights reserved. Patent pending.
        <a
          href="https://about.leap.ly/assets/privacy_policy.pdf"
          :class="themes.candidate.light.link"
        >
          Privacy Policy
        </a>
        |
        <a
          href="https://about.leap.ly/assets/terms_and_conditions.pdf"
          :class="themes.candidate.light.link"
        >
          Terms and conditions
        </a>
      </v-flex>
    </v-footer>
  </v-app>
</template>

<script>
// Components
import CandidateNavigationBar from "@/components/candidate/NavigationBar";
import EmailConfirmationBanner from "@/components/candidate/EmailConfirmationBanner";
import LocationSelect from "@/components/selects/LeaplyLocationSelect";
import Login from "@/components/Login";
import Logo from "@/components/Logo";
import Navigation from "@/components/Navigation";
import Signup from "@/views/candidates/auth/Signup";
import SuspendedBanner from "@/components/candidate/SuspendedBanner";

// Helpers
import { mapGetters, mapState } from "vuex";

// Vue Component
export default {
  name: "Leaply",

  components: {
    CandidateNavigationBar,
    EmailConfirmationBanner,
    LocationSelect,
    Login,
    Logo,
    Navigation,
    Signup,
    SuspendedBanner
  },

  created() {
    if (
      this.$route.hash == "#blocked" &&
      window.CRISP_READY &&
      !window.$crisp.is("chat:opened")
    ) {
      window.$crisp.push(["do", "chat:open"]);
    }
    if (this.$route.hash == "#signup") {
      this.signup = true;
    }
  },

  data() {
    return {
      signup: false,
      showLogin: false,
      location: null
    };
  },

  computed: {
    minimalLayout() {
      return this.$route && this.$route.meta && this.$route.meta.minimal;
    },
    whiteLogo() {
      if (this.minimalLayout) return false;
      if (this.$vuetify.breakpoint.smAndDown) return false;
      return [
        "/",
        "/employers",
        /\/organizations\/\d+/,
        /^(\/client)?\/opportunities\/\d+$$/
      ].some(path => {
        if (typeof path === "string") {
          return path === this.$route.path;
        } else {
          return path.test(this.$route.path);
        }
      });
    },
    ...mapGetters({
      fullName: "auth/fullName",
      loggedIn: "auth/loggedIn",
      role: "auth/role",
      snackbar: "snackbar",
      isPaymentWhitelisted: "client/payments/isWhitelisted"
    }),
    ...mapState({ update: "update" })
  },

  methods: {
    handleLocationSelect(location) {
      this.location = location;
      const latitude = _.get(location, "lat");
      const longitude = _.get(location, "lon");

      if (latitude && longitude) {
        const uri = encodeURI(`/opportunities?center=${[latitude, longitude]}`);
        this.$router.push(uri);
      }
    }
  }
};
</script>

<style lang="scss">
.v-toolbar--absolute > .v-toolbar__content {
  margin: auto;

  @media only screen and (min-width: 960px) {
    max-width: 900px;
  }
  @media only screen and (min-width: 1264px) {
    max-width: 1185px;
  }
  @media only screen and (min-width: 1904px) {
    max-width: 1785px;
  }
}
</style>
